<script lang="ts" setup>
import {
  ComputedRef,
  inject,
  onBeforeMount,
  onUnmounted,
  ref,
  computed,
  PropType,
  watch,
  onMounted,
} from 'vue';
import { CurrentUser } from 'tir-pm-user-core';
import { BarsArrowDownIcon, BarsArrowUpIcon, PlusIcon } from 'tir-style-system/icons/outline';
import {
  TirPmButton,
  TirPmButtonVariantEnum,
  TirPmButtonSizeEnum,
  TirPmScrollbar,
  TirPmIconView,
  TirPmTooltipElementPlacementEnum,
  TirPmTooltipElement,
  TirPmSearchInput,
} from 'tir-components';
import { TirPmSystemMessage } from 'tir-pm-system-message';

import { projectInjectKey } from '@/inject-keys';
import { CreateNamespaceDrawer, CreateNamespaceResultModel } from '@/components';

import { AdapterService } from './adapter';
import { NamespaceCard } from './components';
import { NamespaceModel } from './models';
import { CreateNamespaceInfo, DeleteNamespaceInfo, UpdateNamespaceInfo } from './types';
import { AdminModel } from '../project-settings-admin-page/models';
import { debounce } from 'tir-pm-debounce';
import ProjectNamespaceListRequest from '@/components/tasks-kanban/adapter/TaskKanbanAdapterService';
import { Skeleton } from './components/skeleton';

const adapter = new AdapterService();

/**Ссылка на шторку создания неймспейса */
const $namespaceDrawer = ref<InstanceType<typeof CreateNamespaceDrawer>>();
/**Проект */
const project = inject(projectInjectKey);
/**Админ проекта */
const isAdminProject = inject('isAdminProject') as ComputedRef<boolean>;
/**Загрузка */
const isLoading = ref(false);
/**Состояние сортировки по названию */
const stateSortName = ref(true);
/**Состояние сортировки по кол-ву задач */
const stateSortCountTasks = ref(false);
/**Выбрана сортировка по названию */
const chooseSortName = ref(true);
/**Выбрана сортировка по кол-ву задач */
const chooseSortCountTasks = ref(false);
/**Поиск */
const search = ref();

/**Список неймспейсов */
const namespaces = ref(new Array<NamespaceModel>());

/**Админ проекта */
const isProjectAdmin = computed(() => {
  const x = CurrentUser;
  const rez = project.value.Admins.find((el) => el.Id == x.Id);
  return rez ? true : false;
});
/**Овнер */
const isOwner = computed(() => {
  const x = CurrentUser;
  const rez = x?.Roles.includes(2);
  return rez ? true : false;
});

/**Получить список неймспейсов */
const getNamespaces = async (_search?: string) => {
  isLoading.value = true;
  const resp = await adapter.getNamespaces(
    new ProjectNamespaceListRequest({
      ProjectId: Number(project.value.Id),
      Search: _search,
    }),
  );
  isLoading.value = false;
  if (!resp.IsSuccess) return;

  namespaces.value = resp.Value;
};
/**Поиск НС */
const searchNamespace = debounce(() => getNamespaces(search.value), 300);
/**При удалении неймспейса */
const onDeleteNamespace = (data: DeleteNamespaceInfo) => {
  namespaces.value = namespaces.value.filter((namespace) => namespace.Id !== data.NamespaceId);
};
/**При создании неймспейса */
const onCreateNamespace = async (data: NamespaceModel) => {
  if (!data.ProjectId || data.ProjectId === project.value.Id) {
    namespaces.value.push(data);
  }
};
/**При обновлении неймспейса */
const onUpdateNamespace = async (data: Partial<NamespaceModel>) => {
  // Неймспейс из списка
  const existNamespace = namespaces.value.find((namespace) => namespace.Id === data.Id);
  if (existNamespace) Object.assign(existNamespace, data);
};
/**Подписаться/отписаться на/от события */
const toggleEvents = (isSubscribe: boolean) => {
  const eventFnName = isSubscribe ? 'on' : 'removeListener';

  adapter.CreateNamespaceEvent[eventFnName](onCreateNamespace);
  adapter.DeleteNamespaceEvent[eventFnName](onDeleteNamespace);
  adapter.UpdateNamespaceEvent[eventFnName](onUpdateNamespace);
};

onBeforeMount(async () => {
  await getNamespaces();
  sortByNameNamespaces();

  toggleEvents(true);
});

onUnmounted(() => {
  toggleEvents(false);
});

/**Сортировать НС по названию */
const sortByNameNamespaces = () => {
  if (stateSortName.value) {
    namespaces.value.sort((a, b) => a.Name.localeCompare(b.Name));
  } else {
    namespaces.value.sort((a, b) => b.Name.localeCompare(a.Name));
  }
  stateSortName.value = !stateSortName.value;
  stateSortCountTasks.value = true;
  chooseSortName.value = true;
  chooseSortCountTasks.value = false;
};

/**Сортировать НС по кол-ву задач */
const sortByCountTasksNamespaces = () => {
  if (stateSortCountTasks.value) {
    namespaces.value.sort((a, b) => a.TasksCount - b.TasksCount);
  } else {
    namespaces.value.sort((a, b) => b.TasksCount - a.TasksCount);
  }
  stateSortCountTasks.value = !stateSortCountTasks.value;
  stateSortName.value = true;
  chooseSortName.value = false;
  chooseSortCountTasks.value = true;
};
</script>

<template>
  <div class="project-namespaces-page">
    <div class="namespace-header">
      <h2 class="project-namespaces-page__title">Рабочие области</h2>
      <div class="project-namespaces-page__settings">
        <TirPmSearchInput
          isSmall
          v-model="search"
          @update:modelValue="searchNamespace"
          :isWithPopup="false"
          placeholder=""
          label="Поиск по названию"
        />
        <CreateNamespaceDrawer :project-id="project.Id" v-if="isProjectAdmin || isOwner">
          <template #default="{ open }">
            <TirPmButton
              class="tasks-kanban__header__add"
              :variant="TirPmButtonVariantEnum.SecondaryState"
              :size="TirPmButtonSizeEnum.Small"
              @click="open"
            >
              <template #leftSlot><PlusIcon /></template>
              <p class="create-namespace-drawer__tooltip">Создать рабочую область</p>
            </TirPmButton>
          </template>
        </CreateNamespaceDrawer>
        <TirPmButton
          :variant="TirPmButtonVariantEnum.LinkState"
          :size="TirPmButtonSizeEnum.Small"
          :isPressed="chooseSortName"
          @click="sortByNameNamespaces"
        >
          <template #rightSlot>
            <BarsArrowUpIcon v-if="stateSortName" />
            <BarsArrowDownIcon v-else />
          </template>
          По названию
        </TirPmButton>
        <TirPmButton
          :variant="TirPmButtonVariantEnum.LinkState"
          :size="TirPmButtonSizeEnum.Small"
          :isPressed="chooseSortCountTasks"
          @click="sortByCountTasksNamespaces"
        >
          <template #rightSlot>
            <BarsArrowUpIcon v-if="stateSortCountTasks" />
            <BarsArrowDownIcon v-else />
          </template>
          По количеству задач
        </TirPmButton>
      </div>
    </div>
    <Skeleton v-if="isLoading" class="skeleton" />
    <template v-else>
      <div v-if="namespaces.length" style="width: 100%">
        <TirPmScrollbar :style="{ maxHeight: 'calc(100vh - 15rem)' }" :show-on-hover="true">
          <div class="project-namespaces-page__list" style="margin-right: 0.5rem">
            <NamespaceCard v-for="item in namespaces" :key="item.Id" :namespace-item="item" />
          </div>
        </TirPmScrollbar>
      </div>
      <div v-else class="project-namespaces-page__empty">
        <TirPmSystemMessage style="margin: auto">
          <template #topDescImage>
            <div v-if="project.ImageUrl">
              <img :src="project.ImageUrl" class="project-namespaces-page__empty-project-logo" />
            </div>
            <div v-else>
              <TirPmIconView
                :size="64"
                isRounded
                :isBordered="false"
                :imageLoadUrl="project.ImageUrl"
                :colorCode="project.PreviewColor"
              >
                <template #mainSlot>
                  {{ project?.Name[0].toUpperCase() }}
                </template>
              </TirPmIconView>
            </div>
          </template>
          <template #mainTitleSlot>В проекте пусто</template>
          <template #actionsSlot>
            <TirPmButton
              v-if="isOwner || isAdminProject"
              :variant="TirPmButtonVariantEnum.PrimaryState"
              :size="TirPmButtonSizeEnum.Regular"
              @click="$namespaceDrawer?.open"
            >
              Создать рабочую область
            </TirPmButton>
          </template>
        </TirPmSystemMessage>
      </div>
    </template>
    <CreateNamespaceDrawer ref="$namespaceDrawer" :project-id="project.Id" />
  </div>
</template>

<style lang="scss" scoped>
.skeleton {
  margin-top: 1.5rem;
}
.project-namespaces-page {
  background-color: var(--neutral-0);
  border-radius: 0.5rem;
  height: 100%; // min-height: calc(100vh - 9.475rem);
  &__empty {
    height: calc(100% - 3.0625rem);
  }
  .namespace-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  &__settings {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
  &__title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.75rem;
  }

  &__list {
    display: flex;
    flex-direction: column;
    row-gap: 0.75rem;
    margin-top: 1.5rem;
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    width: 100%;
  }
  &__empty-project-logo {
    display: block;
    width: 5rem;
    height: 5rem;
    border-radius: 1rem;
  }
}
</style>
